import React, { Component } from 'react'
import { withStyles } from '@material-ui/core/styles'
import Paper from '@material-ui/core/Paper'
import Typography from '@material-ui/core/Typography'
import Grid from '@material-ui/core/Grid'
import FormControl from '@material-ui/core/FormControl'
import FormGroup from '@material-ui/core/FormGroup'
import Select from 'react-select'
import Button from '@material-ui/core/Button'
import TextField from '@material-ui/core/TextField'
import Checkbox from '@material-ui/core/Checkbox'
import Tabs from '@material-ui/core/Tabs'
import Tab from '@material-ui/core/Tab'
import Icon from '@material-ui/core/Icon'
import { CircularProgress } from '@material-ui/core'
import gql from 'graphql-tag'
import { Query, Mutation } from 'react-apollo'
import getLoginId from '../../../utils/getLoginId'
import ConfirmationalDialog from '../../common/ConfirmationDialog'

// Same literal solution codes that participate in the OBD > Fuel > VT device-tier model on the
// backend (addDeviceTop / removeDeviceTop). Anything else in solution_type is an independent
// product code that never drives a device replacement.
const TIER_SOLUTION_CODES = new Set([
  'obd_solution',
  'fuel_solution',
  'video_telematics',
  'obd_fuel+fuel_solution',
  'obd_solution+fuel_solution',
  'fuel+vt',
  'obd+vt',
  'fuel+obd+vt'
])

// Normalizes a possibly-mixed solution_type array (atomic codes and/or legacy combo codes like
// "obd_fuel+fuel_solution" stored as a single element) into obd/fuel/vt capability booleans, so
// both representations of "this vehicle has obd+fuel" collapse to the same picker options.
const getCapabilityFlags = (arr) => {
  const flags = { obd: false, fuel: false, vt: false }
  if (!Array.isArray(arr)) return flags
  arr.forEach(code => {
    if (code === 'obd_solution' || code === 'obd_fuel+fuel_solution' || code === 'obd_solution+fuel_solution' || code === 'obd+vt' || code === 'fuel+obd+vt') {
      flags.obd = true
    }
    if (code === 'fuel_solution' || code === 'obd_fuel+fuel_solution' || code === 'obd_solution+fuel_solution' || code === 'fuel+vt' || code === 'fuel+obd+vt') {
      flags.fuel = true
    }
    if (code === 'video_telematics' || code === 'fuel+vt' || code === 'obd+vt' || code === 'fuel+obd+vt') {
      flags.vt = true
    }
  })
  return flags
}

// OBD > Fuel > none/VT - same priority ladder addDeviceTop/removeDeviceTop use on the backend.
const requiredDeviceTier = (flags) => {
  if (flags.obd) return 'obd'
  if (flags.fuel) return 'fuel'
  return 'none'
}

const SolutionOption = props => {
  const { onSelect, onFocus, isSelected, option, children, className } = props
  const handleMouseDown = event => {
    event.preventDefault()
    event.stopPropagation()
    onSelect(option, event)
  }
  const handleMouseEnter = event => {
    onFocus(option, event)
  }

  return (
    <div
      className={className}
      onMouseDown={handleMouseDown}
      onMouseEnter={handleMouseEnter}
      onMouseMove={handleMouseEnter}
      style={{ display: 'flex', alignItems: 'center' }}
    >
      <Checkbox checked={!!isSelected} color="primary" />
      <span>{children}</span>
    </div>
  )
}

const GET_CLIENTS = gql`
  query allClientDetails($partnerLoginId: Int) {
    clients: allClientDetails(partnerLoginId: $partnerLoginId) {
      id
      clientName
      loginId
    }
  }
`

const GET_VEHICLES = gql`
  query getAllVehicleDetails($clientLoginId: Int) {
    getAllVehicleDetails(clientLoginId: $clientLoginId) {
      entityId
      vehicleNumber
      solutionType
      solution_type
      deviceDetail {
        uniqueDeviceId
      }
      simDetail {
        id
        phoneNumber
      }
      videoTelematicsType
      videoTelematicsId
    }
  }
`

const GET_SOLUTION_TYPES = gql`
  query GetAllMasterSolutionType {
    getAllMasterSolutionType {
      id
      solutionTypeName
    }
  }
`

// Query to fetch available devices assigned to the selected customer
const GET_AVAILABLE_DEVICES = gql`
  query getAllAssignedDevicesToClient($clientId: Int!) {
    device: getAllAssignedDevicesToClient(clientLoginId: $clientId) {
      uniqueDeviceId
      deviceModel {
        model_name
      }
    }
  }
`

// Query to fetch available SIMs (phone numbers) assigned to the selected customer
const GET_AVAILABLE_SIMS = gql`
  query getAllAssignedSimToClient($clientId: Int!) {
    simDetails: getAllAssignedSimToClient(clientLoginId: $clientId) {
      id
      simId
      sim {
        phoneNumber
      }
    }
  }
`

const REMOVE_DEVICE_TOP = gql`
  mutation RemoveDeviceTop(
    $clientLoginId: Int
    $vehicledeviceid: Int
    $removeSolutionType: String!
    $newUniqueDeviceId: String
    $newSimId: Int
    $isDeviceAndSimDecoupleNotRequired: Boolean
    $loginType: String
    $reason: String
    $created_by: String
    $updated_by: String
  ) {
    removeDeviceTop(
      clientLoginId: $clientLoginId
      vehicledeviceid: $vehicledeviceid
      removeSolutionType: $removeSolutionType
      newUniqueDeviceId: $newUniqueDeviceId
      newSimId: $newSimId
      isDeviceAndSimDecoupleNotRequired: $isDeviceAndSimDecoupleNotRequired
      loginType: $loginType
      reason: $reason
      created_by: $created_by
      updated_by: $updated_by
    ) {
      success
      message
    }
  }
`

const VT_REMOVAL_ONBOARDING = gql`
  mutation vtremovalonboarding(
    $existingvtid: String
    $solution_type: String
    $existinguniqueid: String
    $created_by: String
    $updated_by: String
  ) {
    vtremovalonboarding(
      existingvtid: $existingvtid
      solution_type: $solution_type
      existinguniqueid: $existinguniqueid
      created_by: $created_by
      updated_by: $updated_by
    ) {
      success
      message
    }
  }
`

const UPDATE_VEHICLE_SOLUTION_TYPE = gql`
  mutation UpdateVehicleSolutionType(
    $clientLoginId: Int
    $vehicledeviceid: Int
    $solutionType: String
    $solution_type: [String]!
    $reason: String
    $created_by: String
    $updated_by: String
  ) {
    updateVehicleSolutionType(
      clientLoginId: $clientLoginId
      vehicledeviceid: $vehicledeviceid
      solutionType: $solutionType
      solution_type: $solution_type
      reason: $reason
      created_by: $created_by
      updated_by: $updated_by
    ) {
      success
      message
    }
  }
`

const styles = theme => ({
  root: {
    padding: 24,
    marginTop: 24,
    backgroundColor: '#fafafa',
    minHeight: '100vh'
  },
  paper: {
    padding: 24,
    marginBottom: 20,
    borderRadius: 8,
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
  },
  formControl: {
    marginTop: 8,
    width: '100%'
  },
  selectBox: {
    width: '100%'
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: 16,
    color: '#2c3e50'
  },
  vehicleCard: {
    background: '#ffffff',
    padding: 16,
    borderRadius: 8,
    border: '1px solid #e0e0e0',
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)'
  },
  infoRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '8px 0',
    borderBottom: '1px solid #f0f0f0',
    '&:last-child': { borderBottom: 'none' }
  },
  infoLabel: {
    fontWeight: 600,
    color: '#555',
    minWidth: 160,
    fontSize: 13
  },
  infoValue: {
    color: '#222',
    fontSize: 13
  },
  solutionChip: {
    display: 'inline-block',
    padding: '3px 10px',
    borderRadius: 20,
    background: '#e3f2fd',
    color: '#1565c0',
    border: '1px solid #90caf9',
    marginRight: 6,
    marginTop: 4,
    fontSize: 12,
    fontWeight: 500
  },
  warningBox: {
    backgroundColor: '#fff3e0',
    border: '1px solid #ff9800',
    borderRadius: 6,
    padding: '10px 16px',
    marginBottom: 16,
    display: 'flex',
    alignItems: 'center',
    gap: 8
  },
  blockedBox: {
    backgroundColor: '#ffebee',
    border: '1px solid #ef9a9a',
    borderRadius: 6,
    padding: '12px 16px',
    display: 'flex',
    alignItems: 'center',
    gap: 8
  },
  submitBtn: {
    minWidth: 170,
    height: 40,
    backgroundColor: '#c62828',
    color: '#fff',
    '&:hover': { backgroundColor: '#7f0000' },
    '&:disabled': { backgroundColor: '#bdbdbd', color: '#fff' }
  },
  cancelBtn: {
    minWidth: 120,
    height: 40,
    marginRight: 10
  }
})

class SolutionWiseUnregistration extends Component {
  constructor(props) {
    super(props)
    this.state = {
      activeTab: 'unregister',
      selectedCustomer: null,
      selectedVehicle: null,

      // Tab 1 - Solution-Wise Unregistration
      selectedRemovals: [],
      reason: '',
      selectedNewDevice: null,
      selectedNewSimId: null,

      // Tab 2 - VT ID Unregistration
      vtConfirmDialogOpen: false,

      // Tab 3 - Update Solution Type
      updateSelectedSolutionTypes: [],
      updateSolutionType: '',
      updateReason: '',
      updateConfirmDialogOpen: false,

      confirmDialogOpen: false,
      confirmDialogMessage: '',
      alertDialogOpen: false,
      alertDialogMessage: '',
      alertDialogTitle: ''
    }
  }

  handleTabChange = (event, activeTab) => {
    if (!activeTab) return
    this.setState({
      activeTab,
      selectedCustomer: null,
      selectedVehicle: null,
      selectedRemovals: [],
      reason: '',
      selectedNewDevice: null,
      selectedNewSimId: null,
      updateSelectedSolutionTypes: [],
      updateSolutionType: '',
      updateReason: ''
    })
  }

  handleCustomerChange = (selectedCustomer) => {
    this.setState({
      selectedCustomer,
      selectedVehicle: null,
      selectedRemovals: [],
      reason: '',
      selectedNewDevice: null,
      selectedNewSimId: null,
      updateSelectedSolutionTypes: [],
      updateSolutionType: '',
      updateReason: ''
    })
  }

  handleVehicleChange = (selectedVehicle) => {
    const existingSolutions = selectedVehicle && Array.isArray(selectedVehicle.solution_type)
      ? selectedVehicle.solution_type
      : []
    this.setState({
      selectedVehicle,
      selectedRemovals: [],
      reason: '',
      selectedNewDevice: null,
      selectedNewSimId: null,
      // Update Solution Type tab pre-fills from the vehicle's current solution_type, mirroring
      // how Vehicle Edit shows what's currently set.
      updateSelectedSolutionTypes: existingSolutions,
      updateSolutionType: selectedVehicle ? (selectedVehicle.solutionType || '') : '',
      updateReason: ''
    })
  }

  // ───────────────────────── Tab 1: Solution-Wise Unregistration ─────────────────────────

  handleRemovalsChange = (selectedRemovals) => {
    this.setState({
      selectedRemovals: selectedRemovals || [],
      selectedNewDevice: null,
      selectedNewSimId: null
    })
  }

  handleNewDeviceChange = (selectedOption) => {
    this.setState({ selectedNewDevice: selectedOption })
  }

  handleNewSimChange = (selectedOption) => {
    this.setState({ selectedNewSimId: selectedOption ? selectedOption.value : null })
  }

  // Recomputes the same before/after device-tier comparison the backend uses, so the UI can
  // require a replacement device+SIM up front instead of failing only after submit.
  computeNeedsReplacement = (existingFlags, removingCodes) => {
    if (!removingCodes || removingCodes.length === 0) return false
    const removingFlags = {
      obd: removingCodes.includes('obd_solution'),
      fuel: removingCodes.includes('fuel_solution'),
      vt: removingCodes.includes('video_telematics')
    }
    const remainingFlags = {
      obd: existingFlags.obd && !removingFlags.obd,
      fuel: existingFlags.fuel && !removingFlags.fuel,
      vt: existingFlags.vt && !removingFlags.vt
    }
    return requiredDeviceTier(existingFlags) !== requiredDeviceTier(remainingFlags)
  }

  handleSubmitClick = (removableOptionsCount) => {
    const { selectedCustomer, selectedVehicle, selectedRemovals, reason, selectedNewDevice, selectedNewSimId } = this.state

    if (!selectedCustomer || !selectedVehicle || !selectedRemovals || selectedRemovals.length === 0) {
      this.setState({
        alertDialogOpen: true,
        alertDialogTitle: 'Validation Error',
        alertDialogMessage: 'Please select a vehicle and at least one solution type to unregister'
      })
      return
    }

    if (!reason || !reason.trim()) {
      this.setState({
        alertDialogOpen: true,
        alertDialogTitle: 'Validation Error',
        alertDialogMessage: 'Please provide a reason for unregistration'
      })
      return
    }

    if (selectedRemovals.length >= removableOptionsCount) {
      this.setState({
        alertDialogOpen: true,
        alertDialogTitle: 'Validation Error',
        alertDialogMessage: 'Cannot unregister all solution types on this vehicle. At least one must remain - use Vehicle Unregistration to fully unregister the vehicle instead.'
      })
      return
    }

    const existingSolutions = Array.isArray(selectedVehicle.solution_type) ? selectedVehicle.solution_type : []
    const existingFlags = getCapabilityFlags(existingSolutions)
    const removingCodes = selectedRemovals.map(r => r.value)
    const needsReplacement = this.computeNeedsReplacement(existingFlags, removingCodes)

    if (needsReplacement && (!selectedNewDevice || !selectedNewSimId)) {
      this.setState({
        alertDialogOpen: true,
        alertDialogTitle: 'Validation Error',
        alertDialogMessage: 'Removing this solution requires downgrading the GPS device. Please select a replacement device and SIM.'
      })
      return
    }

    const solutionLabels = selectedRemovals.map(r => r.label).join(', ')
    const replacementNote = needsReplacement
      ? ` This will also replace the existing device with ${selectedNewDevice.value} and SIM ${selectedNewSimId}.`
      : ''
    const vtNote = removingCodes.includes('video_telematics') ? ' The Video Telematics ID assigned to this vehicle will also be unregistered.' : ''
    this.setState({
      confirmDialogOpen: true,
      confirmDialogMessage: `Are you sure you want to unregister ${solutionLabels} from ${selectedVehicle.label}?${replacementNote}${vtNote}`
    })
  }

  executeSubmit = () => {
    const { selectedCustomer, selectedVehicle, selectedRemovals, reason, selectedNewDevice, selectedNewSimId } = this.state

    const existingSolutions = Array.isArray(selectedVehicle.solution_type) ? selectedVehicle.solution_type : []
    const existingFlags = getCapabilityFlags(existingSolutions)
    const removingCodes = selectedRemovals.map(r => r.value)
    const needsReplacement = this.computeNeedsReplacement(existingFlags, removingCodes)

    this.setState({ confirmDialogOpen: false })

    if (!this.pendingMutate) return

    this.pendingMutate({
      variables: {
        clientLoginId: selectedCustomer.value,
        vehicledeviceid: parseInt(selectedVehicle.entityId, 10),
        removeSolutionType: removingCodes.join('+'),
        newUniqueDeviceId: needsReplacement ? selectedNewDevice.value : null,
        newSimId: needsReplacement ? selectedNewSimId : null,
        isDeviceAndSimDecoupleNotRequired: false,
        loginType: localStorage.getItem('Account_type') || 'PA',
        reason: reason.trim(),
        created_by: localStorage.getItem('username'),
        updated_by: localStorage.getItem('username')
      }
    })
      .then((result) => {
        const { success, message } = result.data.removeDeviceTop
        if (success) {
          this.setState({
            alertDialogOpen: true,
            alertDialogTitle: 'Success',
            alertDialogMessage: message || 'Solution unregistered successfully!',
            selectedCustomer: null,
            selectedVehicle: null,
            selectedRemovals: [],
            reason: '',
            selectedNewDevice: null,
            selectedNewSimId: null
          })
        } else {
          this.setState({
            alertDialogOpen: true,
            alertDialogTitle: 'Error',
            alertDialogMessage: message || 'Failed to unregister the solution'
          })
        }
      })
      .catch((error) => {
        console.error('Solution unregistration error:', error)
        const errorMessage = error.graphQLErrors && error.graphQLErrors.length > 0
          ? error.graphQLErrors[0].message
          : error.message || 'Failed to unregister the solution'
        this.setState({
          alertDialogOpen: true,
          alertDialogTitle: 'Error',
          alertDialogMessage: errorMessage
        })
      })
  }

  // ───────────────────────── Tab 2: VT ID Unregistration ─────────────────────────

  handleVtSubmitClick = () => {
    const { selectedCustomer, selectedVehicle } = this.state
    if (!selectedCustomer || !selectedVehicle) {
      this.setState({
        alertDialogOpen: true,
        alertDialogTitle: 'Validation Error',
        alertDialogMessage: 'Please select a vehicle'
      })
      return
    }
    if (!selectedVehicle.videoTelematicsId) {
      this.setState({
        alertDialogOpen: true,
        alertDialogTitle: 'Validation Error',
        alertDialogMessage: 'Selected vehicle has no Video Telematics ID to unregister'
      })
      return
    }
    this.setState({ vtConfirmDialogOpen: true })
  }

  executeVtSubmit = () => {
    const { selectedVehicle } = this.state
    this.setState({ vtConfirmDialogOpen: false })

    if (!this.pendingVtMutate) return

    this.pendingVtMutate({
      variables: {
        existingvtid: selectedVehicle.videoTelematicsId,
        solution_type: Array.isArray(selectedVehicle.solution_type) ? selectedVehicle.solution_type.join(',') : (selectedVehicle.solution_type || null),
        existinguniqueid: selectedVehicle.deviceDetail && selectedVehicle.deviceDetail.uniqueDeviceId,
        created_by: localStorage.getItem('username'),
        updated_by: localStorage.getItem('username')
      }
    })
      .then((result) => {
        const { success, message } = result.data.vtremovalonboarding
        this.setState({
          alertDialogOpen: true,
          alertDialogTitle: success ? 'Success' : 'Error',
          alertDialogMessage: message || (success ? 'VT ID unregistered successfully!' : 'Failed to unregister VT ID'),
          ...(success ? { selectedCustomer: null, selectedVehicle: null } : {})
        })
      })
      .catch((error) => {
        console.error('VT unregistration error:', error)
        const errorMessage = error.graphQLErrors && error.graphQLErrors.length > 0
          ? error.graphQLErrors[0].message
          : error.message || 'Failed to unregister VT ID'
        this.setState({
          alertDialogOpen: true,
          alertDialogTitle: 'Error',
          alertDialogMessage: errorMessage
        })
      })
  }

  closeVtConfirmDialog = () => {
    this.setState({ vtConfirmDialogOpen: false })
  }

  // ───────────────────────── Tab 3: Update Solution Type ─────────────────────────

  // Compute legacy solutionType string from new solution_type array codes - ported verbatim from
  // Vehicle Registration (VehicleRegistartion/index.jsx computeLegacySolutionType) so a vehicle's
  // solutionType is always derived the same way regardless of where it's edited from.
  computeLegacySolutionType(solutionCodes) {
    const codeToMaster = {
      fuel: 'fuel_solution',
      obd: 'obd_solution',
      vt: 'video_telematics',
      obdanalogsolution: 'obd_analog_solution',
      basic_tracking: 'basic_tracking',
      ais_140: 'ais_140',
      asset_tracking: 'Asset Tracking',
      control_tower: 'control_tower',
      testing_vehicle: 'testing_vehicle',
      'fuel+vt': 'fuel+vt',
      'obd+vt': 'obd+vt',
      'fuel+obd+vt': 'fuel+obd+vt',
      reverse_camera_screen: 'reverse_camera_screen'
    }

    if (!solutionCodes || solutionCodes.length === 0) {
      return ''
    }

    if (solutionCodes.length === 1) {
      const code = solutionCodes[0]
      return codeToMaster[code] || code
    }

    const hasFuel = solutionCodes.includes('fuel_solution')
    const hasObd = solutionCodes.includes('obd_solution')
    const hasVt = solutionCodes.includes('video_telematics')

    if (hasFuel && hasObd && hasVt) {
      return 'fuel+obd+vt'
    }
    if (hasFuel && hasVt) {
      return 'fuel+vt'
    }
    if (hasObd && hasVt) {
      return 'obd+vt'
    }
    if (hasFuel && hasObd) {
      return 'obd_fuel+fuel_solution'
    }
    if (hasVt) {
      if (hasFuel) return 'fuel+vt'
      if (hasObd) return 'obd+vt'
    }

    const firstCode = solutionCodes[0]
    return codeToMaster[firstCode] || firstCode
  }

  handleUpdateSolutionTypesChange = (selected) => {
    if (!selected) {
      this.setState({ updateSelectedSolutionTypes: [], updateSolutionType: '' })
      return
    }
    const selectedCodes = selected.map(opt => opt.code)
    const legacySolutionType = this.computeLegacySolutionType(selectedCodes)
    this.setState({
      updateSelectedSolutionTypes: selectedCodes,
      updateSolutionType: legacySolutionType
    })
  }

  handleUpdateSubmitClick = () => {
    const { selectedCustomer, selectedVehicle, updateSelectedSolutionTypes, updateReason } = this.state
    if (!selectedCustomer || !selectedVehicle) {
      this.setState({
        alertDialogOpen: true,
        alertDialogTitle: 'Validation Error',
        alertDialogMessage: 'Please select a vehicle'
      })
      return
    }
    if (!updateSelectedSolutionTypes || updateSelectedSolutionTypes.length === 0) {
      this.setState({
        alertDialogOpen: true,
        alertDialogTitle: 'Validation Error',
        alertDialogMessage: 'Please select at least one solution type'
      })
      return
    }
    if (!updateReason || !updateReason.trim()) {
      this.setState({
        alertDialogOpen: true,
        alertDialogTitle: 'Validation Error',
        alertDialogMessage: 'Please provide a reason for updating the solution type'
      })
      return
    }
    this.setState({ updateConfirmDialogOpen: true })
  }

  executeUpdateSubmit = () => {
    const { selectedCustomer, selectedVehicle, updateSelectedSolutionTypes, updateSolutionType, updateReason } = this.state
    this.setState({ updateConfirmDialogOpen: false })

    if (!this.pendingUpdateMutate) return

    this.pendingUpdateMutate({
      variables: {
        clientLoginId: selectedCustomer.value,
        vehicledeviceid: parseInt(selectedVehicle.entityId, 10),
        solutionType: updateSolutionType,
        solution_type: updateSelectedSolutionTypes,
        reason: updateReason.trim(),
        created_by: localStorage.getItem('username'),
        updated_by: localStorage.getItem('username')
      }
    })
      .then((result) => {
        const { success, message } = result.data.updateVehicleSolutionType
        this.setState({
          alertDialogOpen: true,
          alertDialogTitle: success ? 'Success' : 'Error',
          alertDialogMessage: message || (success ? 'Solution type updated successfully!' : 'Failed to update solution type'),
          ...(success ? { selectedCustomer: null, selectedVehicle: null, updateSelectedSolutionTypes: [], updateSolutionType: '', updateReason: '' } : {})
        })
      })
      .catch((error) => {
        console.error('Update solution type error:', error)
        const errorMessage = error.graphQLErrors && error.graphQLErrors.length > 0
          ? error.graphQLErrors[0].message
          : error.message || 'Failed to update solution type'
        this.setState({
          alertDialogOpen: true,
          alertDialogTitle: 'Error',
          alertDialogMessage: errorMessage
        })
      })
  }

  closeUpdateConfirmDialog = () => {
    this.setState({ updateConfirmDialogOpen: false })
  }

  // ───────────────────────── Shared ─────────────────────────

  closeConfirmDialog = () => {
    this.setState({ confirmDialogOpen: false })
  }

  closeAlertDialog = () => {
    this.setState({ alertDialogOpen: false })
  }

  renderCustomerVehicleSelectors(classes, vehicleFilter) {
    const { selectedCustomer, selectedVehicle } = this.state
    const partnerLoginId = getLoginId()

    return (
      <Paper className={classes.paper}>
        <Grid container spacing={3}>
          {/* Customer Dropdown */}
          <Grid item xs={12} sm={6}>
            <FormControl className={classes.formControl} fullWidth>
              <FormGroup>
                <Typography variant="subtitle1" gutterBottom>
                  Select Customer *
                </Typography>
                <Query query={GET_CLIENTS} variables={{ partnerLoginId }}>
                  {({ loading, error, data }) => {
                    if (loading) return <CircularProgress size={24} />
                    if (error) return <div>Error loading customers</div>

                    const customerOptions = data.clients.map(client => ({
                      value: client.loginId,
                      label: client.clientName,
                      ...client
                    }))

                    return (
                      <Select
                        value={selectedCustomer}
                        onChange={this.handleCustomerChange}
                        options={customerOptions}
                        placeholder="Select a customer"
                        className={classes.selectBox}
                      />
                    )
                  }}
                </Query>
              </FormGroup>
            </FormControl>
          </Grid>

          {/* Vehicle Dropdown */}
          <Grid item xs={12} sm={6}>
            <FormControl className={classes.formControl} fullWidth>
              <FormGroup>
                <Typography variant="subtitle1" gutterBottom>
                  Select Vehicle *
                </Typography>
                {selectedCustomer ? (
                  <Query query={GET_VEHICLES} variables={{ clientLoginId: selectedCustomer.value }}>
                    {({ loading, error, data }) => {
                      if (loading) return <CircularProgress size={24} />
                      if (error) return <div>Error loading vehicles</div>

                      let vehicleOptions = data.getAllVehicleDetails.map(vehicle => ({
                        value: vehicle.entityId,
                        label: vehicle.vehicleNumber,
                        ...vehicle
                      }))

                      if (vehicleFilter) {
                        vehicleOptions = vehicleOptions.filter(vehicleFilter)
                      }

                      return (
                        <Select
                          value={selectedVehicle}
                          onChange={this.handleVehicleChange}
                          options={vehicleOptions}
                          placeholder="Select a vehicle"
                          className={classes.selectBox}
                        />
                      )
                    }}
                  </Query>
                ) : (
                  <div style={{ color: '#666', padding: '8px' }}>
                    Please select a customer first
                  </div>
                )}
              </FormGroup>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>
    )
  }

  renderUnregisterTab(classes) {
    const {
      selectedCustomer,
      selectedVehicle,
      selectedRemovals,
      reason,
      selectedNewDevice,
      selectedNewSimId
    } = this.state

    return (
      <React.Fragment>
        {this.renderCustomerVehicleSelectors(classes)}

        {selectedVehicle && (
          <Query query={GET_SOLUTION_TYPES}>
            {({ loading: solLoading, error: solError, data: solData }) => {
              if (solLoading) return <Paper className={classes.paper}><CircularProgress size={24} /></Paper>
              if (solError) return <Paper className={classes.paper}>Error loading solution types</Paper>

              const masterSolutionTypes = solData.getAllMasterSolutionType || []
              // Same label source the Solution Topup dropdown uses - getAllMasterSolutionType's
              // solutionTypeName - so a code reads identically ("OBD Solution") in both places.
              const masterLabel = code => {
                const master = masterSolutionTypes.find(s => s.id === code)
                return master ? master.solutionTypeName : code
              }

              const existingSolutions = Array.isArray(selectedVehicle.solution_type) ? selectedVehicle.solution_type : []
              const existingFlags = getCapabilityFlags(existingSolutions)
              const otherExisting = [...new Set(existingSolutions.filter(c => c && !TIER_SOLUTION_CODES.has(c)))]

              const removableOptions = []
              if (existingFlags.obd) removableOptions.push({ value: 'obd_solution', label: masterLabel('obd_solution') })
              if (existingFlags.fuel) removableOptions.push({ value: 'fuel_solution', label: masterLabel('fuel_solution') })
              if (existingFlags.vt) removableOptions.push({ value: 'video_telematics', label: masterLabel('video_telematics') })
              otherExisting.forEach(code => {
                removableOptions.push({ value: code, label: masterLabel(code) })
              })

              const onlyOneSolution = removableOptions.length <= 1
              const removingCodes = selectedRemovals.map(r => r.value)
              const needsReplacement = this.computeNeedsReplacement(existingFlags, removingCodes)
              // Selecting every remaining solution (not just the single-solution case above) would
              // leave the vehicle with none - block that here too, not just at submit time.
              const wouldRemoveAll = selectedRemovals.length > 0 && selectedRemovals.length >= removableOptions.length

              return (
                <React.Fragment>
                  {/* Vehicle Details */}
                  <Paper className={classes.paper}>
                    <Typography variant="h6" className={classes.sectionTitle}>
                      Vehicle Details
                    </Typography>
                    <div className={classes.vehicleCard}>
                      <div className={classes.infoRow}>
                        <span className={classes.infoLabel}>Vehicle Number</span>
                        <span className={classes.infoValue}>{selectedVehicle.vehicleNumber}</span>
                      </div>
                      <div className={classes.infoRow}>
                        <span className={classes.infoLabel}>Device Unique ID</span>
                        <span className={classes.infoValue} style={{ fontFamily: 'monospace', fontSize: 12 }}>
                          {(selectedVehicle.deviceDetail && selectedVehicle.deviceDetail.uniqueDeviceId) || 'Not Installed'}
                        </span>
                      </div>
                      <div className={classes.infoRow}>
                        <span className={classes.infoLabel}>Existing Solutions</span>
                        <span className={classes.infoValue}>
                          {existingSolutions.length > 0 ? (
                            existingSolutions.map((sol, i) => (
                              <span key={i} className={classes.solutionChip}>{sol}</span>
                            ))
                          ) : (
                            <span style={{ color: '#999', fontSize: 12 }}>No solutions assigned</span>
                          )}
                        </span>
                      </div>
                    </div>
                  </Paper>

                  {/* Solution Removal */}
                  <Paper className={classes.paper}>
                    <Typography variant="h6" gutterBottom>
                      Remove Solution Type
                    </Typography>

                    {onlyOneSolution ? (
                      <div className={classes.blockedBox}>
                        <Icon style={{ color: '#c62828', fontSize: 22 }}>block</Icon>
                        <Typography variant="body2" style={{ color: '#c62828', fontWeight: 500 }}>
                          This vehicle has only one solution type ({removableOptions[0] ? removableOptions[0].label : 'none'}).
                          It cannot be unregistered here - use Vehicle Unregistration to fully unregister the vehicle instead.
                        </Typography>
                      </div>
                    ) : (
                      <React.Fragment>
                        <Grid container spacing={3}>
                          <Grid item xs={12}>
                            <FormControl className={classes.formControl} fullWidth>
                              <FormGroup>
                                <Typography variant="subtitle1" gutterBottom>
                                  Solution Type(s) to Remove *
                                </Typography>
                                <Select
                                  id="remove_solution_type"
                                  name="remove_solution_type"
                                  options={removableOptions.filter(opt => {
                                    const alreadySelected = selectedRemovals.some(r => r.value === opt.value)
                                    if (alreadySelected) return true
                                    // Don't offer an option whose selection would leave the vehicle
                                    // with no solution type at all.
                                    return (selectedRemovals.length + 1) < removableOptions.length
                                  })}
                                  value={selectedRemovals}
                                  multi
                                  optionComponent={SolutionOption}
                                  closeOnSelect={false}
                                  removeSelected={false}
                                  onChange={this.handleRemovalsChange}
                                  placeholder="Select solution type(s) to unregister"
                                  className={classes.selectBox}
                                />
                                {wouldRemoveAll && (
                                  <div className={classes.blockedBox} style={{ marginTop: 12 }}>
                                    <Icon style={{ color: '#c62828', fontSize: 22 }}>block</Icon>
                                    <Typography variant="body2" style={{ color: '#c62828', fontWeight: 500 }}>
                                      This would remove every solution type on this vehicle. At least one must remain -
                                      deselect one, or use Vehicle Unregistration to fully unregister the vehicle instead.
                                    </Typography>
                                  </div>
                                )}
                              </FormGroup>
                            </FormControl>
                          </Grid>
                        </Grid>

                        {needsReplacement && (
                          <Grid container spacing={3} style={{ marginTop: 16 }}>
                            <Grid item xs={12}>
                              <Typography variant="caption" style={{ color: '#f57c00', display: 'block', marginBottom: 4 }}>
                                Note: Removing the selected solution type lowers the existing device id tier this vehicle needs. Replacing the device also requires replacing the SIM.
                              </Typography>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                              <FormControl className={classes.formControl} fullWidth>
                                <FormGroup>
                                  <Typography variant="subtitle1" gutterBottom>
                                    Select New Device ID *
                                  </Typography>
                                  <Query
                                    query={GET_AVAILABLE_DEVICES}
                                    variables={{ clientId: selectedCustomer.value }}
                                    fetchPolicy="network-only"
                                  >
                                    {({ loading: devicesLoading, error: devicesError, data: devicesData }) => {
                                      if (devicesLoading) return <CircularProgress size={24} />
                                      if (devicesError) return <div>Error loading available devices</div>

                                      const devices = devicesData && devicesData.device ? devicesData.device : []
                                      const deviceOptions = devices.map(device => ({
                                        value: device.uniqueDeviceId,
                                        label: device.uniqueDeviceId,
                                        model: device.deviceModel ? device.deviceModel.model_name : null
                                      }))

                                      const currentDeviceId = selectedVehicle.deviceDetail && selectedVehicle.deviceDetail.uniqueDeviceId
                                      const filteredOptions = currentDeviceId
                                        ? deviceOptions.filter(opt => opt.value !== currentDeviceId)
                                        : deviceOptions

                                      return (
                                        <Select
                                          value={selectedNewDevice}
                                          onChange={this.handleNewDeviceChange}
                                          options={filteredOptions}
                                          placeholder="Choose a new device ID"
                                          className={classes.selectBox}
                                          isClearable
                                        />
                                      )
                                    }}
                                  </Query>
                                </FormGroup>
                              </FormControl>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                              <FormControl className={classes.formControl} fullWidth>
                                <FormGroup>
                                  <Typography variant="subtitle1" gutterBottom>
                                    Select New Phone Number *
                                  </Typography>
                                  <Query
                                    query={GET_AVAILABLE_SIMS}
                                    variables={{ clientId: selectedCustomer.value }}
                                    fetchPolicy="network-only"
                                  >
                                    {({ loading: simsLoading, error: simsError, data: simsData }) => {
                                      if (simsLoading) return <CircularProgress size={24} />
                                      if (simsError) return <div>Error loading available SIMs</div>

                                      const sims = simsData && simsData.simDetails ? simsData.simDetails : []
                                      const simOptions = sims
                                        .filter(sim => sim.sim)
                                        .map(sim => ({
                                          value: sim.simId,
                                          label: sim.sim.phoneNumber
                                        }))

                                      const selectedSimOption = selectedNewSimId
                                        ? simOptions.find(opt => opt.value === selectedNewSimId)
                                        : null

                                      return (
                                        <Select
                                          value={selectedSimOption}
                                          onChange={this.handleNewSimChange}
                                          options={simOptions}
                                          placeholder="Choose a phone number"
                                          className={classes.selectBox}
                                          isClearable
                                        />
                                      )
                                    }}
                                  </Query>
                                </FormGroup>
                              </FormControl>
                            </Grid>
                          </Grid>
                        )}

                        {selectedRemovals.length > 0 && (
                          <Grid container spacing={2} style={{ marginTop: 16 }} alignItems="flex-end">
                            <Grid item xs={12} sm={9}>
                              <TextField
                                label="Reason for Unregistration *"
                                placeholder="Enter reason for unregistering this solution..."
                                value={reason}
                                onChange={e => this.setState({ reason: e.target.value })}
                                multiline
                                rows={2}
                                fullWidth
                                variant="outlined"
                                size="small"
                              />
                            </Grid>
                          </Grid>
                        )}
                      </React.Fragment>
                    )}
                  </Paper>

                  {/* Action Buttons */}
                  {!onlyOneSolution && (
                    <Paper className={classes.paper}>
                      <Grid container justify="flex-end" alignItems="center" spacing={2}>
                        <Grid item>
                          <Mutation mutation={REMOVE_DEVICE_TOP}>
                            {(removeDeviceTop, { loading }) => {
                              this.pendingMutate = removeDeviceTop
                              return (
                                <Button
                                  variant="contained"
                                  className={classes.submitBtn}
                                  onClick={() => this.handleSubmitClick(removableOptions.length)}
                                  disabled={
                                    loading ||
                                    !selectedRemovals ||
                                    selectedRemovals.length === 0 ||
                                    wouldRemoveAll ||
                                    !reason.trim() ||
                                    (needsReplacement && (!selectedNewDevice || !selectedNewSimId))
                                  }
                                >
                                  {loading
                                    ? <CircularProgress size={20} style={{ color: '#fff' }} />
                                    : 'Unregister Solution'}
                                </Button>
                              )
                            }}
                          </Mutation>
                        </Grid>
                      </Grid>
                    </Paper>
                  )}
                </React.Fragment>
              )
            }}
          </Query>
        )}
      </React.Fragment>
    )
  }

  renderVtRemovalTab(classes) {
    const { selectedVehicle } = this.state

    return (
      <React.Fragment>
        <Paper className={classes.paper}>
          <Typography variant="body2" style={{ color: '#666' }}>
            This removes only the Video Telematics (VT) ID assigned to the vehicle - the vehicle's unique ID is not affected.
            To fully unregister the vehicle, use Vehicle Unregistration instead.
          </Typography>
        </Paper>

        {this.renderCustomerVehicleSelectors(classes, v => v.videoTelematicsId)}

        {selectedVehicle && (
          <Paper className={classes.paper}>
            <Typography variant="h6" className={classes.sectionTitle}>
              Vehicle Details
            </Typography>
            <div className={classes.vehicleCard}>
              <div className={classes.infoRow}>
                <span className={classes.infoLabel}>Vehicle Number</span>
                <span className={classes.infoValue}>{selectedVehicle.vehicleNumber}</span>
              </div>
              <div className={classes.infoRow}>
                <span className={classes.infoLabel}>Video Telematics Type</span>
                <span className={classes.infoValue}>{selectedVehicle.videoTelematicsType || 'N/A'}</span>
              </div>
              <div className={classes.infoRow}>
                <span className={classes.infoLabel}>Video Telematics ID</span>
                <span className={classes.infoValue} style={{ fontFamily: 'monospace', fontSize: 12 }}>
                  {selectedVehicle.videoTelematicsId || 'Not Assigned'}
                </span>
              </div>
            </div>
          </Paper>
        )}

        {selectedVehicle && (
          <Paper className={classes.paper}>
            <Grid container justify="flex-end" alignItems="center" spacing={2}>
              <Grid item>
                <Mutation mutation={VT_REMOVAL_ONBOARDING}>
                  {(vtremovalonboarding, { loading }) => {
                    this.pendingVtMutate = vtremovalonboarding
                    return (
                      <Button
                        variant="contained"
                        className={classes.submitBtn}
                        onClick={this.handleVtSubmitClick}
                        disabled={loading || !selectedVehicle.videoTelematicsId}
                      >
                        {loading
                          ? <CircularProgress size={20} style={{ color: '#fff' }} />
                          : 'Unregister VT ID'}
                      </Button>
                    )
                  }}
                </Mutation>
              </Grid>
            </Grid>
          </Paper>
        )}
      </React.Fragment>
    )
  }

  renderUpdateSolutionTypeTab(classes) {
    const { selectedVehicle, updateSelectedSolutionTypes, updateReason } = this.state

    return (
      <React.Fragment>
        {this.renderCustomerVehicleSelectors(classes)}

        {selectedVehicle && (
          <Query query={GET_SOLUTION_TYPES}>
            {({ loading, error, data }) => {
              if (loading) return <Paper className={classes.paper}><CircularProgress size={24} /></Paper>
              if (error) return <Paper className={classes.paper}>Error loading solution types</Paper>

              const solutionTypeMultiOptions = data.getAllMasterSolutionType.map(type => ({
                value: type.id,
                label: type.solutionTypeName,
                code: type.id
              }))

              const selectedMultiValue = solutionTypeMultiOptions.filter(
                option => updateSelectedSolutionTypes && updateSelectedSolutionTypes.includes(option.code)
              )

              return (
                <React.Fragment>
                  <Paper className={classes.paper}>
                    <Typography variant="h6" className={classes.sectionTitle}>
                      Vehicle Details
                    </Typography>
                    <div className={classes.vehicleCard}>
                      <div className={classes.infoRow}>
                        <span className={classes.infoLabel}>Vehicle Number</span>
                        <span className={classes.infoValue}>{selectedVehicle.vehicleNumber}</span>
                      </div>
                      <div className={classes.infoRow}>
                        <span className={classes.infoLabel}>Device Unique ID</span>
                        <span className={classes.infoValue} style={{ fontFamily: 'monospace', fontSize: 12 }}>
                          {(selectedVehicle.deviceDetail && selectedVehicle.deviceDetail.uniqueDeviceId) || 'Not Installed'}
                        </span>
                      </div>
                      <div className={classes.infoRow}>
                        <span className={classes.infoLabel}>Existing Solutions</span>
                        <span className={classes.infoValue}>
                          {Array.isArray(selectedVehicle.solution_type) && selectedVehicle.solution_type.length > 0 ? (
                            selectedVehicle.solution_type.map((sol, i) => (
                              <span key={i} className={classes.solutionChip}>{sol}</span>
                            ))
                          ) : (
                            <span style={{ color: '#999', fontSize: 12 }}>No solutions assigned</span>
                          )}
                        </span>
                      </div>
                    </div>
                  </Paper>

                  <Paper className={classes.paper}>
                    <Typography variant="h6" gutterBottom>
                      Update Solution Type
                    </Typography>
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <FormControl className={classes.formControl} fullWidth>
                          <FormGroup>
                            <Typography variant="subtitle1" gutterBottom>
                              Solution Type(s) *
                            </Typography>
                            <Select
                              id="update_solution_type"
                              name="update_solution_type"
                              options={solutionTypeMultiOptions}
                              value={selectedMultiValue}
                              multi
                              optionComponent={SolutionOption}
                              closeOnSelect={false}
                              removeSelected={false}
                              onChange={this.handleUpdateSolutionTypesChange}
                              placeholder="Select solution type(s)"
                              className={classes.selectBox}
                            />
                          </FormGroup>
                        </FormControl>
                      </Grid>
                    </Grid>

                    <Grid container spacing={2} style={{ marginTop: 16 }} alignItems="flex-end">
                      <Grid item xs={12} sm={9}>
                        <TextField
                          label="Reason for Update *"
                          placeholder="Enter reason for updating the solution type..."
                          value={updateReason}
                          onChange={e => this.setState({ updateReason: e.target.value })}
                          multiline
                          rows={2}
                          fullWidth
                          variant="outlined"
                          size="small"
                        />
                      </Grid>
                    </Grid>
                  </Paper>

                  <Paper className={classes.paper}>
                    <Grid container justify="flex-end" alignItems="center" spacing={2}>
                      <Grid item>
                        <Mutation mutation={UPDATE_VEHICLE_SOLUTION_TYPE}>
                          {(updateVehicleSolutionType, { loading: updateLoading }) => {
                            this.pendingUpdateMutate = updateVehicleSolutionType
                            return (
                              <Button
                                variant="contained"
                                className={classes.submitBtn}
                                onClick={this.handleUpdateSubmitClick}
                                disabled={updateLoading || !updateSelectedSolutionTypes || updateSelectedSolutionTypes.length === 0 || !updateReason || !updateReason.trim()}
                              >
                                {updateLoading
                                  ? <CircularProgress size={20} style={{ color: '#fff' }} />
                                  : 'Update Solution Type'}
                              </Button>
                            )
                          }}
                        </Mutation>
                      </Grid>
                    </Grid>
                  </Paper>
                </React.Fragment>
              )
            }}
          </Query>
        )}
      </React.Fragment>
    )
  }

  render() {
    const { classes } = this.props
    const {
      activeTab,
      confirmDialogOpen,
      confirmDialogMessage,
      vtConfirmDialogOpen,
      updateConfirmDialogOpen,
      alertDialogOpen,
      alertDialogMessage,
      alertDialogTitle
    } = this.state

    return (
      <div className={classes.root}>
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          style={{ fontWeight: 700, marginBottom: 20 }}
        >
          Solution-Wise Unregistration
        </Typography>

        <Paper className={classes.paper}>
          <Tabs
            value={activeTab}
            onChange={this.handleTabChange}
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab value="unregister" label="Solution-Wise Unregistration" />
            <Tab value="vt" label="VT ID Unregistration" />
            <Tab value="update" label="Update Solution Type" />
          </Tabs>
        </Paper>

        {activeTab === 'unregister' && this.renderUnregisterTab(classes)}
        {activeTab === 'vt' && this.renderVtRemovalTab(classes)}
        {activeTab === 'update' && this.renderUpdateSolutionTypeTab(classes)}

        {/* Confirm Dialog - Tab 1 */}
        <ConfirmationalDialog
          isDialogOpen={confirmDialogOpen}
          dialogTitle="Confirm Solution Unregistration"
          dialogMessage={confirmDialogMessage}
          positiveResponseButtonText="Yes, Unregister"
          negativeResponseButtonText="Cancel"
          positiveResponseHandler={this.executeSubmit}
          negativeResponseHandler={this.closeConfirmDialog}
          dialogCloseHandler={this.closeConfirmDialog}
        />

        {/* Confirm Dialog - Tab 2 */}
        <ConfirmationalDialog
          isDialogOpen={vtConfirmDialogOpen}
          dialogTitle="Confirm VT ID Unregistration"
          dialogMessage={this.state.selectedVehicle ? `Are you sure you want to unregister VT ID ${this.state.selectedVehicle.videoTelematicsId} from ${this.state.selectedVehicle.label}?` : ''}
          positiveResponseButtonText="Yes, Unregister"
          negativeResponseButtonText="Cancel"
          positiveResponseHandler={this.executeVtSubmit}
          negativeResponseHandler={this.closeVtConfirmDialog}
          dialogCloseHandler={this.closeVtConfirmDialog}
        />

        {/* Confirm Dialog - Tab 3 */}
        <ConfirmationalDialog
          isDialogOpen={updateConfirmDialogOpen}
          dialogTitle="Confirm Solution Type Update"
          dialogMessage={this.state.selectedVehicle ? `Are you sure you want to update the solution type for ${this.state.selectedVehicle.label} to "${this.state.updateSolutionType}"?` : ''}
          positiveResponseButtonText="Yes, Update"
          negativeResponseButtonText="Cancel"
          positiveResponseHandler={this.executeUpdateSubmit}
          negativeResponseHandler={this.closeUpdateConfirmDialog}
          dialogCloseHandler={this.closeUpdateConfirmDialog}
        />

        {/* Alert Dialog */}
        <ConfirmationalDialog
          isDialogOpen={alertDialogOpen}
          dialogTitle={alertDialogTitle}
          dialogMessage={alertDialogMessage}
          positiveResponseButtonText="OK"
          negativeResponseButtonText=""
          positiveResponseHandler={this.closeAlertDialog}
          negativeResponseHandler={this.closeAlertDialog}
          dialogCloseHandler={this.closeAlertDialog}
        />
      </div>
    )
  }
}

export default withStyles(styles)(SolutionWiseUnregistration)



// WEBPACK FOOTER //
// ./src/components/Pages/SolutionWiseUnregistration/index.jsx